import {IForm} from '../pages/oneAddQuote/oneQuoteTypes'
import {IFormSold} from '../pages/onePolicy/soldTypes'
import {getYearFromEffectiveDate} from '.'

/**
 * Semantic Versioning (MAJOR.MINOR.PATCH)
 *
 * MAJOR (v2.0.0): Breaking changes - incompatible API changes, major redesigns
 * MINOR (v1.6.0): New features - backwards-compatible functionality additions
 * PATCH (v1.5.1): Bug fixes - backwards-compatible bug fixes
 *
 * Examples:
 * - v1.5.1: Fixed archive permission bug (PATCH - bug fix)
 * - v1.6.0: Added notification system & appointment display in leads (MINOR - new features)
 * - v1.7.0: Added user avatar system, performance optimizations for PoliciesPage (MINOR - new features)
 * - v2.0.0: Complete UI overhaul (MAJOR - breaking change)
 */
export const VERSION_APP = 'v1.23.0'

// ===== Brand switch (toggle) =====
export const IS_TFI = true // <- cambia a true para The Future Insurance
const BRAND = IS_TFI
  ? ({
      BACKEND_URL: 'https://app-backend.policytools.com',
      CLIENT_DISPLAY_NAME: 'The Future Insurance',
      CLIENT_SHORT_NAME: 'TFI',
      CLIENT_SITE: 'https://www.thefutureinsurance.com/',
      CLIENT_COUNTRY: 'United States',
      CLIENT_LOGO: '/media/tfi/logo_policytools.png',
      FEATURE_SHOW_GOOGLE_INTEGRATION: true,
    } as const)
  : ({
      BACKEND_URL: 'https://backend.policytools.com',
      CLIENT_DISPLAY_NAME: 'Test Apps Centers',
      CLIENT_SHORT_NAME: 'AHC',
      CLIENT_SITE: 'https://www.policytools.com/',
      CLIENT_COUNTRY: 'United States',
      //CLIENT_LOGO: '/media/tfi/logoN.png'
      CLIENT_LOGO: '/media/tfi/logo_policytools.png',
      FEATURE_SHOW_GOOGLE_INTEGRATION: false,
    } as const)

const normalizeBooleanEnv = (value?: string) => {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim().toLowerCase()
  if (!normalized) return undefined
  if (['true', '1', 'yes', 'on'].includes(normalized)) return true
  if (['false', '0', 'no', 'off'].includes(normalized)) return false
  return undefined
}

export const BACKEND_URL = BRAND.BACKEND_URL
// ===== Tenant/Brand (provisional; will move to DB later) =====
export const CLIENT_DISPLAY_NAME = BRAND.CLIENT_DISPLAY_NAME
export const CLIENT_SHORT_NAME = BRAND.CLIENT_SHORT_NAME
export const CLIENT_SITE = BRAND.CLIENT_SITE
export const CLIENT_COUNTRY = BRAND.CLIENT_COUNTRY
export const CLIENT_LOGO = BRAND.CLIENT_LOGO

// =============================================================
const RAW_FEATURE_SHOW_GOOGLE_INTEGRATION = normalizeBooleanEnv(
  process.env.REACT_APP_FEATURE_SHOW_GOOGLE_INTEGRATION,
)

export const FEATURE_SHOW_GOOGLE_INTEGRATION =
  RAW_FEATURE_SHOW_GOOGLE_INTEGRATION ?? BRAND.FEATURE_SHOW_GOOGLE_INTEGRATION

const DEFAULT_GOOGLE_CLIENT_ID =
  '1005174958588-ht8kplhn013evfjg2de734oc2upk6djp.apps.googleusercontent.com'

export const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || DEFAULT_GOOGLE_CLIENT_ID

export const RING_CENTRAL_CLIENT_ID = 'Z9YGOPXDJkVfVDDW7RM8ll'
export const RING_CENTRAL_APP_SERVER =
  process.env.NODE_ENV === 'production'
    ? 'https://platform.ringcentral.com'
    : 'https://platform.devtest.ringcentral.com'

export const STORAGE_NAMES = {
  TOKEN: 'TOKEN',
  USER: 'USER',
  PERMISSIONS: 'PERMISSIONS',
  ID_USER: 'USER_ID',
  LAST_ROUTE: 'LAST_ROUTE',
  TOKEN_GOOGLE: 'google_token',
  EMAIL_GOOGLE: 'google_email',
  RING_CENTRAL: 'ringcentral',
} as const

export const EVENT_SERVICE_WORKER_NAME = 'swUpdated' as const
export const MSG_SKIP_WAITING = 'SKIP_WAITING' as const

export const INIT_FORM: IFormSold = {
  policyholder: {
    isApplicant: 'yes',
    firstName: '',
    lastName: '',
    dateBirth: '',
    email: '',
    maritalStatus: '',
    weight: '',
    language: 'Spanish',
    middleName: '',
    secondLastName: '',
    gender: 'Male',
    phone: '',
    countryBirth: '',
    height: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    zipcode: '',
    country: '',
    mailingAddressLine1: '',
    mailingAddressLine2: '',
    mailingCity: '',
    mailingStateProvince: '',
    mailingZipCode: '',
    mailingCounty: '',
    immigrationStatus: '',
    uscis: '',
    ssn: '',
    greenCard: '',
    workPermitCard: '',
    driverLicense: '',
    immigrationStatusCategory: '',
    passport: '',
    ssnIssuedDate: '',
    greenCardIssueDate: '',
    greenCardExpirationDate: '',
    workPermitIssueDate: '',
    workPermitExpirationDate: '',
    driverLicenseExpirationDate: '',
    employerName1: '',
    selfEmployed1: false,
    employerPhone1: '',
    positionOccupation1: '',
    annualIncome1: '',
    employerName2: '',
    selfEmployed2: false,
    employerPhone2: '',
    positionOccupation2: '',
    annualIncome2: '',
    employerName3: '',
    selfEmployed3: false,
    employerPhone3: '',
    positionOccupation3: '',
    annualIncome3: '',
  },
  familyMembers: {
    numberApplicants: '',
    numberMembers: '',
    numberDependents: '',
    familyMember: [],
  },
  insurancePayments: {
    ffmUsedMarketplace: '',
    npnUsedMarketplace: '',
    typeSale: '',
    effectiveDate: '',
    marketplaceID: '',
    memberID: '',
    cmsPlanID: '',
    Carrier: '',
    planName: '',
    metalLevel: '',
    policyTotalCost: '',
    taxCreditSubsidy: '',
    premium: '',
    recurrentPayment: '',
    firstPaymentDate: '',
    preferredPaymentDayEachMonth: '',
    creditDebit: '',
    bankEFT: '',
    cardCompany: '',
    bankName: '',
    paymentMethodHolder: '',
    cardNumber: '',
    abaRouting: '',
    addressLine1: '',
    addressLine2: '',
    expirationMonth: '',
    expirationYear: '',
    accountNumber: '',
    city: '',
    cvvCsv: '',
    stateProvince: '',
    zipCode: '',
  },
  extras: {
    primaryDoctorInformation: '',
    medicinesNeeded: '',
    extraNotesComments: '',
    cancellationDate: '',
    specialEnrollmentDate: '',
    specialEnrollmentReason: '',
  },
}

export const COUNTRIES = [
  {label: 'Afghanistan', value: 'AF'},
  {label: 'Albania', value: 'AL'},
  {label: 'Algeria', value: 'DZ'},
  {label: 'Andorra', value: 'AD'},
  {label: 'Angola', value: 'AO'},
  {label: 'Antigua and Barbuda', value: 'AG'},
  {label: 'Argentina', value: 'AR'},
  {label: 'Armenia', value: 'AM'},
  {label: 'Australia', value: 'AU'},
  {label: 'Austria', value: 'AT'},
  {label: 'Azerbaijan', value: 'AZ'},
  {label: 'Bahamas', value: 'BS'},
  {label: 'Bahrain', value: 'BH'},
  {label: 'Bangladesh', value: 'BD'},
  {label: 'Barbados', value: 'BB'},
  {label: 'Belarus', value: 'BY'},
  {label: 'Belgium', value: 'BE'},
  {label: 'Belize', value: 'BZ'},
  {label: 'Benin', value: 'BJ'},
  {label: 'Bhutan', value: 'BT'},
  {label: 'Bolivia', value: 'BO'},
  {label: 'Bosnia and Herzegovina', value: 'BA'},
  {label: 'Botswana', value: 'BW'},
  {label: 'Brazil', value: 'BR'},
  {label: 'Brunei', value: 'BN'},
  {label: 'Bulgaria', value: 'BG'},
  {label: 'Burkina Faso', value: 'BF'},
  {label: 'Burundi', value: 'BI'},
  {label: 'Cabo Verde', value: 'CV'},
  {label: 'Cambodia', value: 'KH'},
  {label: 'Cameroon', value: 'CM'},
  {label: 'Canada', value: 'CA'},
  {label: 'Central African Republic', value: 'CF'},
  {label: 'Chad', value: 'TD'},
  {label: 'Chile', value: 'CL'},
  {label: 'China', value: 'CN'},
  {label: 'Colombia', value: 'CO'},
  {label: 'Comoros', value: 'KM'},
  {label: 'Congo (Congo-Brazzaville)', value: 'CG'},
  {label: 'Congo (Democratic Republic)', value: 'CD'},
  {label: 'Costa Rica', value: 'CR'},
  {label: 'Croatia', value: 'HR'},
  {label: 'Cuba', value: 'CU'},
  {label: 'Cyprus', value: 'CY'},
  {label: 'Czechia', value: 'CZ'},
  {label: 'Denmark', value: 'DK'},
  {label: 'Djibouti', value: 'DJ'},
  {label: 'Dominica', value: 'DM'},
  {label: 'Dominican Republic', value: 'DO'},
  {label: 'Ecuador', value: 'EC'},
  {label: 'Egypt', value: 'EG'},
  {label: 'El Salvador', value: 'SV'},
  {label: 'Equatorial Guinea', value: 'GQ'},
  {label: 'Eritrea', value: 'ER'},
  {label: 'Estonia', value: 'EE'},
  {label: 'Eswatini', value: 'SZ'},
  {label: 'Ethiopia', value: 'ET'},
  {label: 'Fiji', value: 'FJ'},
  {label: 'Finland', value: 'FI'},
  {label: 'France', value: 'FR'},
  {label: 'Gabon', value: 'GA'},
  {label: 'Gambia', value: 'GM'},
  {label: 'Georgia', value: 'GE'},
  {label: 'Germany', value: 'DE'},
  {label: 'Ghana', value: 'GH'},
  {label: 'Greece', value: 'GR'},
  {label: 'Grenada', value: 'GD'},
  {label: 'Guatemala', value: 'GT'},
  {label: 'Guinea', value: 'GN'},
  {label: 'Guinea-Bissau', value: 'GW'},
  {label: 'Guyana', value: 'GY'},
  {label: 'Haiti', value: 'HT'},
  {label: 'Honduras', value: 'HN'},
  {label: 'Hungary', value: 'HU'},
  {label: 'Iceland', value: 'IS'},
  {label: 'India', value: 'IN'},
  {label: 'Indonesia', value: 'ID'},
  {label: 'Iran', value: 'IR'},
  {label: 'Iraq', value: 'IQ'},
  {label: 'Ireland', value: 'IE'},
  {label: 'Israel', value: 'IL'},
  {label: 'Italy', value: 'IT'},
  {label: 'Jamaica', value: 'JM'},
  {label: 'Japan', value: 'JP'},
  {label: 'Jordan', value: 'JO'},
  {label: 'Kazakhstan', value: 'KZ'},
  {label: 'Kenya', value: 'KE'},
  {label: 'Kiribati', value: 'KI'},
  {label: 'Kuwait', value: 'KW'},
  {label: 'Kyrgyzstan', value: 'KG'},
  {label: 'Laos', value: 'LA'},
  {label: 'Latvia', value: 'LV'},
  {label: 'Lebanon', value: 'LB'},
  {label: 'Lesotho', value: 'LS'},
  {label: 'Liberia', value: 'LR'},
  {label: 'Libya', value: 'LY'},
  {label: 'Liechtenstein', value: 'LI'},
  {label: 'Lithuania', value: 'LT'},
  {label: 'Luxembourg', value: 'LU'},
  {label: 'Madagascar', value: 'MG'},
  {label: 'Malawi', value: 'MW'},
  {label: 'Malaysia', value: 'MY'},
  {label: 'Maldives', value: 'MV'},
  {label: 'Mali', value: 'ML'},
  {label: 'Malta', value: 'MT'},
  {label: 'Marshall Islands', value: 'MH'},
  {label: 'Mauritania', value: 'MR'},
  {label: 'Mauritius', value: 'MU'},
  {label: 'Mexico', value: 'MX'},
  {label: 'Micronesia', value: 'FM'},
  {label: 'Moldova', value: 'MD'},
  {label: 'Monaco', value: 'MC'},
  {label: 'Mongolia', value: 'MN'},
  {label: 'Montenegro', value: 'ME'},
  {label: 'Morocco', value: 'MA'},
  {label: 'Mozambique', value: 'MZ'},
  {label: 'Myanmar', value: 'MM'},
  {label: 'Namibia', value: 'NA'},
  {label: 'Nauru', value: 'NR'},
  {label: 'Nepal', value: 'NP'},
  {label: 'Netherlands', value: 'NL'},
  {label: 'New Zealand', value: 'NZ'},
  {label: 'Nicaragua', value: 'NI'},
  {label: 'Niger', value: 'NE'},
  {label: 'Nigeria', value: 'NG'},
  {label: 'North Korea', value: 'KP'},
  {label: 'North Macedonia', value: 'MK'},
  {label: 'Norway', value: 'NO'},
  {label: 'Oman', value: 'OM'},
  {label: 'Pakistan', value: 'PK'},
  {label: 'Palau', value: 'PW'},
  {label: 'Palestine', value: 'PS'},
  {label: 'Panama', value: 'PA'},
  {label: 'Papua New Guinea', value: 'PG'},
  {label: 'Paraguay', value: 'PY'},
  {label: 'Peru', value: 'PE'},
  {label: 'Philippines', value: 'PH'},
  {label: 'Poland', value: 'PL'},
  {label: 'Portugal', value: 'PT'},
  {label: 'Qatar', value: 'QA'},
  {label: 'Romania', value: 'RO'},
  {label: 'Russia', value: 'RU'},
  {label: 'Rwanda', value: 'RW'},
  {label: 'Saint Kitts and Nevis', value: 'KN'},
  {label: 'Saint Lucia', value: 'LC'},
  {label: 'Saint Vincent and the Grenadines', value: 'VC'},
  {label: 'Samoa', value: 'WS'},
  {label: 'San Marino', value: 'SM'},
  {label: 'Sao Tome and Principe', value: 'ST'},
  {label: 'Saudi Arabia', value: 'SA'},
  {label: 'Senegal', value: 'SN'},
  {label: 'Serbia', value: 'RS'},
  {label: 'Seychelles', value: 'SC'},
  {label: 'Sierra Leone', value: 'SL'},
  {label: 'Singapore', value: 'SG'},
  {label: 'Slovakia', value: 'SK'},
  {label: 'Slovenia', value: 'SI'},
  {label: 'Solomon Islands', value: 'SB'},
  {label: 'Somalia', value: 'SO'},
  {label: 'South Africa', value: 'ZA'},
  {label: 'South Korea', value: 'KR'},
  {label: 'South Sudan', value: 'SS'},
  {label: 'Spain', value: 'ES'},
  {label: 'Sri Lanka', value: 'LK'},
  {label: 'Sudan', value: 'SD'},
  {label: 'Suriname', value: 'SR'},
  {label: 'Sweden', value: 'SE'},
  {label: 'Switzerland', value: 'CH'},
  {label: 'Syria', value: 'SY'},
  {label: 'Taiwan', value: 'TW'},
  {label: 'Tajikistan', value: 'TJ'},
  {label: 'Tanzania', value: 'TZ'},
  {label: 'Thailand', value: 'TH'},
  {label: 'Timor-Leste', value: 'TL'},
  {label: 'Togo', value: 'TG'},
  {label: 'Tonga', value: 'TO'},
  {label: 'Trinidad and Tobago', value: 'TT'},
  {label: 'Tunisia', value: 'TN'},
  {label: 'Turkey', value: 'TR'},
  {label: 'Turkmenistan', value: 'TM'},
  {label: 'Tuvalu', value: 'TV'},
  {label: 'Uganda', value: 'UG'},
  {label: 'Ukraine', value: 'UA'},
  {label: 'United Arab Emirates', value: 'AE'},
  {label: 'United Kingdom', value: 'GB'},
  {label: 'United States', value: 'US'},
  {label: 'Uruguay', value: 'UY'},
  {label: 'Uzbekistan', value: 'UZ'},
  {label: 'Vanuatu', value: 'VU'},
  {label: 'Vatican City', value: 'VA'},
  {label: 'Venezuela', value: 'VE'},
  {label: 'Vietnam', value: 'VN'},
  {label: 'Yemen', value: 'YE'},
  {label: 'Zambia', value: 'ZM'},
  {label: 'Zimbabwe', value: 'ZW'},
]

export const COLORS = ['primary', 'success', 'warning', 'danger', 'info', 'dark'] as const

export const bootstrapColorMap: Record<string, string> = {
  primary: '#0d6efd',
  secondary: '#6c757d',
  success: '#198754',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#0dcaf0',
  light: '#f8f9fa',
  dark: '#212529',
  gray: '#adb5bd',

  // Aliases usados en statuses
  blue: '#3e97ff',
  green: '#198754',
  red: '#dc3545',
  orange: '#fd7e14',
  purple: '#6f42c1',
  yellow: '#ffc107',
  accent: '#3e97ff',
}

// Devuelve un hex si es bootstrap, o un color si es válido, o gris como fallback
export function getBootstrapColorHex(color: string): string {
  if (bootstrapColorMap[color]) {
    return bootstrapColorMap[color]
  }

  // Si es un valor válido en CSS (ej: 'green' o '#123456'), lo devolvemos tal cual
  const isCssColor = CSS.supports('color', color)
  return isCssColor ? color : '#adb5bd'
}

export function getContrastTextColor(bgColor: string): string {
  // Convierte el hex a RGB
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  // Calcula luminancia relativa (receta WCAG)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Si es muy claro → texto negro, si es oscuro → texto blanco
  return luminance > 0.6 ? '#000000' : '#ffffff'
}

export const getPolicyCategory = (
  edges: {node: {code: string; category: string}}[] | undefined,
  code?: string
) => edges?.find((e) => e.node.code === (code ?? ''))?.node.category ?? null

export const ALL_PERMISSIONS = {
  VIEW_USER: 'view_user',
  ADD_USER: 'add_user',
  EDIT_USER: 'change_user',

  VIEW_LEAD: 'view_lead',
  ADD_LEAD: 'add_lead',
  DELETE_LEAD: 'delete_lead',
  EDIT_LEAD: 'change_lead',
  EXPORT_LEAD: 'export_lead',

  VIEW_POLICY: 'view_policy',
  VIEW_ALL_POLICIES: 'view_all_policies',
  ADD_POLICY: 'add_policy',
  EDIT_POLICY: 'change_policy',
  VIEW_POLICY_STATUS: 'view_policy_status',
  EXPORT_POLICY: 'export_policy',
  EXPORT_POLICY_FULL: 'export_policy_full',
  MARK_COMPLETED_POLICY: 'mark_completed_policy',
  VIEW_POLICY_PAYMENT: 'view_policy_payment',

  VIEW_SETTING: 'view_setting',

  VIEW_PIPELINE: 'view_pipeline',
  VIEW_LEAD_SOURCE: 'view_lead_source',
  VIEW_GROUP_ASSIGNMENT: 'view_group_assignment',
  VIEW_LEAD_LOSS_REASON: 'view_lead_loss_reason',
  VIEW_TAG_MANAGER: 'view_tag_manager',


  VIEW_COMMISSION: 'view_commission_report',
  ADD_COMMISSION: 'add_commission_report',
  EDIT_COMMISSION: 'change_commission_report',
  DELETE_COMMISSION: 'delete_commission_report',

  VIEW_SIGNATURE_FORMS: 'view_signatureform',
  ADD_SIGNATURE_FORMS: 'add_signatureform',
  EDIT_SIGNATURE_FORMS: 'change_signatureform',
  DELETE_SIGNATURE_FORMS: 'delete_signatureform',

  VIEW_CONTACT: 'view_contact',
  ADD_CONTACT: 'add_contact',
  EDIT_CONTACT: 'change_contact',
  DELETE_CONTACT: 'delete_contact',
  VIEW_CONTACT_PREVIEW_ASSIGNED: 'view_contact_preview_assigned',
  VIEW_COMMUNICATION_LOGS: 'view_policy',
  

  VIEW_SMS: 'view_policy',
  VIEW_EMAIL: 'view_policy',

  VIEW_NOTIFICATION: 'view_notification',

  VIEW_INTEGRATION: 'view_integrations',
  ADD_INTEGRATION: 'add_integrations',
  EDIT_INTEGRATION: 'change_integrations',
  DELETE_INTEGRATION: 'delete_integrations',

  VIEW_CARRIER: 'view_insurance_operator',
  ADD_CARRIER: 'add_insurance_operator',
  EDIT_CARRIER: 'change_insurance_operator',
  DELETE_CARRIER: 'delete_insurance_operator',

  VIEW_POLICY_TYPE: 'view_policy_type',
  ADD_POLICY_TYPE: 'add_policy_type',
  EDIT_POLICY_TYPE: 'change_policy_type',
  DELETE_POLICY_TYPE: 'delete_policy_type',

  VIEW_COMMISSION_DEDUCTION_TYPE: 'view_commission_deduction_type',
  ADD_COMMISSION_DEDUCTION_TYPE: 'add_commission_deduction_type',
  EDIT_COMMISSION_DEDUCTION_TYPE: 'change_commission_deduction_type',
  DELETE_COMMISSION_DEDUCTION_TYPE: 'delete_commission_deduction_type',

  VIEW_RESOURCE_CENTER: 'view_resource_center',
  ADD_RESOURCE_CENTER: 'add_resource_center',
  EDIT_RESOURCE_CENTER: 'change_resource_center',
  DELETE_RESOURCE_CENTER: 'delete_resource_center',

  VIEW_TASKS: 'view_kanbantask',
  ADD_TASKS: 'add_kanbantask',
  EDIT_TASKS: 'change_kanbantask',
  DELETE_TASKS: 'delete_kanbantask',

  VIEW_TICKET: 'view_ticket',
  ADD_TICKET: 'add_ticket',
  CHANGE_TICKET: 'change_ticket',
  DELETE_TICKET: 'delete_ticket',
  ASSIGN_TICKET: 'assign_ticket',
  CHANGE_TICKET_STATUS: 'change_ticket_status',
  COMMENT_TICKET: 'comment_ticket',
  DOWNLOAD_TICKET_ATTACHMENT: 'download_ticket_attachment',
  VIEW_ALL_TICKET: 'view_all_ticket',
  ARCHIVE_TICKET: 'archive_ticket',
  MANAGE_TICKET_SLA: 'manage_ticket_sla',

  VIEW_CAMPAIGNS: 'view_campaign',

} as const

export const TEMPLATES = {
  AGENT: 'AGENT_OF_RECORD_CONSENTIMIENTO_CONSUMIDOR',
  CONTRATO: 'CONTRATO_CONSENTIMIENTO',
  CARTA: 'CARTA_INGRESOS_CUENTA_PROPIA',
} as const

export const GENDER_OPTIONS = [
  {value: 'Male', label: 'Male'},
  {value: 'Female', label: 'Female'},
  {value: 'Other', label: 'Other'},
]

export const INIT_PEOPLE = {
  age: '',
  dob: '',
  gender: 'Male',
  isPregnant: false,
  pregnantWith: '',
  isParent: false,
  usesTobacco: false,
  lastTobaccoUseDate: '',
  hasMec: false,
  aptcEligible: true,
  utilizationLevel: '',
  relationship: '',
  doesNotCohabitate: '',
  currentEnrollment: null,
}

export const SIZE_PAGE_QUICK_QUOTE = 20
export const SIZE_PAGE_DEFAULT = 10
export const SIZE_STEP_PAGINATION = 10

export const INIT_FORM_ATTACHMENTS = {
  name: '',
  attachments: '',
  comments: '',
  documentTagId: '',
  expirationDate: '',
  memberPolicyId: '',
}

export const INIT_FORM_EXTRAS = {
  primaryDoctorInfo: '',
  medicinesNeeded: '',
  infoExtra: '',
  extraNotesComments: '',
  cancellationDate: '',
  specialEnrollmentDate: '',
  specialEnrollmentReason: '',
}

export const INIT_FORMS_PAYMENTS = {
  recurrentPayment: false,
  typeCard: '',
  cardCompany: '',
  cardNumber: '',
  expirationMonth: '',
  expirationDay: '', // puedes mantenerlo como string si usas input
  cvvCsv: '',
  firstPaymentDate: '',
  bankAccount: false,
  bankName: '',
  ABARouting: '',
  accountNumber: '',
  preferredPayment: '', // si usarás select, puedes dejarlo string
  billingAddress: false,
  paymentHolder: '',
  address1: '',
  address2: '',
  city: '',
  zipCode: '',
  stateEU: {id: '', code: '', name: ''},
}

export const INIT_FORM_MEMBERS = {
  preferredLenguage: '',
  applicant: true,
  rol: '',
  firstName: '',
  middleName: '',
  lastName: '',
  secondLastName: '',
  dateBirth: '',
  gender: '',
  emailAddress: '',
  phone: '',
  maritalStatus: '',
  relationship: '',
  countryBirth: '',
  weight: '',
  height: '',
  immigrationStatus: '',
  immigrationStatusCode: '',
  uscis: '',
  passport: '',
  ssn: '',
  ssnIssuedDate: '',
  greenCard: '',
  greenCardIssuedDate: '',
  greenCardExpirationDate: '',
  workPermitCard: '',
  workPermitIssuedDate: '',
  workPermitExpirationDate: '',
  driverLicense: '',
  driverLicenseExpirationDate: '',
  employerName1: '',
  selfEmployed1: false,
  employerPhone1: '',
  positionOccupation1: '',
  annualIncome1: '',
  employerName2: '',
  selfEmployed2: false,
  employerPhone2: '',
  positionOccupation2: '',
  annualIncome2: '',
  employerName3: '',
  selfEmployed3: false,
  employerPhone3: '',
  positionOccupation3: '',
  annualIncome3: '',
  policy: '',
  id: '',
  usesTobacco: false,
  isPregnant: false,
  hasMec: false,
}

export const INIT_FORM_QUOTE_POLICY = {
  id: '',
  created: '',
  updated: '',
  policyName: '',
  state: 'PENDING',
  stateDocs: 'PENDING',
  paymentStatus: 'PENDING',
  FFM: '',
  NPN: '',
  typeSale: '',
  effectiveDate: '',
  marketplaceId: '',
  memberId: '',
  CMSPlanId: '',
  planName: '',
  policyTotalCost: '',
  taxCredit: '',
  premium: '',
  percentageValue: '',
  nominalValue: '',
  ownsCar: null,
  ownsHome: null,
  hasAutoPolicy: null,
  hasHomePolicy: null,
  status: '',
  completedDate: '',
  canceledDate: '',
  commissionPayable: false,
  zipCode: '',
  countyFips: '',
  address1: '',
  address2: '',
  city: '',
  stateEU: {id: '', code: '', name: ''},

  mailingAddress1: '',
  mailingAddress2: '',
  mailingCity: '',
  mailingZipCode: '',
  mailingCountyFips: '',
  mailingStateeu: {id: '', code: '', name: ''},

  stateName: '',
  isImportant: false,
  applicants: '',
  members: '',
  dependents: 0,
  metalLevel: 0,
  // Campos adicionales que no están en `Node` pero usas en el form
  user: {id: ''},
  typePolicy: {id: ''},
  leadSource: {id: ''},
  carrier: {id: ''},
  policyAttachmentsSet: {...INIT_FORM_ATTACHMENTS},
  lead: {},
  assignedTo: {id: 0},
  holder: {...INIT_FORM_MEMBERS},
  policyMembers: [] as typeof INIT_FORM_MEMBERS[],
  policyPayment: {...INIT_FORMS_PAYMENTS},
  policyExtraInfo: {...INIT_FORM_EXTRAS},
  policyMembersDelete: [],
}

export function getPolicyName(values) {
  const first = values.firstName.trim()
  const middle = values.middleName.trim()
  const last = values.lastName.trim()
  const second = values.secondLastName.trim()

  // Toma la inicial en mayúscula si existe, si no, omite
  const middleInitial = middle ? ' ' + middle : ''
  const secondInitial = second ? ' ' + second : ''
  // const middleInitial = middle ? ` ${middle[0].toUpperCase()}.` : ''
  // const secondInitial = second ? ` ${second[0].toUpperCase()}.` : ''

  // Une los componentes
  return `${first}${middleInitial} ${last}${secondInitial}`.replace(/\s+/g, ' ').trim()
}

export function getMetalLevelValueByLabel(label: string): string | undefined {
  const found = METAL_LEVELS.find((level) => level.label === label)
  return found?.value
}

export function getMetalLevelValue(value: string): string | undefined {
  const found = METAL_LEVELS.find((level) => level.value === value)
  return found?.label
}

export const normalizeAmount = (val: any): string => {
  if (typeof val === 'string') {
    return val.replace(/,/g, '') || '0.00'
  }
  return typeof val === 'number' ? val.toFixed(2) : '0.00'
}

export const METAL_LEVELS = [
  {value: '5', label: 'Platinum'},
  {value: '4', label: 'Gold'},
  {value: '3', label: 'Silver'},
  {value: '2', label: 'Bronze'},
  {value: '1', label: 'Catastrophic'},
]

export const MARITAL_STATUS = ['Married', 'Head of Household', 'Single', 'Other']

export const IMMIGRATION_STATUS_CHOICES = [
  {value: 'CITIZEN', label: 'Citizen'},
  {value: 'RESIDENT', label: 'Resident'},
  {value: 'ASYLUM', label: 'Asylum Seeker'},
  {value: 'EAD', label: 'Employment Authorization Document (EAD)'},
  {value: 'TPS', label: 'Temporary Protected Status (TPS)'},
  {value: 'HUMANITARIAN_PAROLE', label: 'Humanitarian Parole'},
  {value: 'OTHER', label: 'Other'},
  {value: 'UNDOCUMENTED', label: 'Undocumented'},
]

export const MONTHS = [
  {val: 1, label: 'Enero'},
  {val: 2, label: 'Febrero'},
  {val: 3, label: 'Marzo'},
  {val: 4, label: 'Abril'},
  {val: 5, label: 'Mayo'},
  {val: 6, label: 'Junio'},
  {val: 7, label: 'Julio'},
  {val: 8, label: 'Agosto'},
  {val: 9, label: 'Septiembre'},
  {val: 10, label: 'Octubre'},
  {val: 11, label: 'Noviembre'},
  {val: 12, label: 'Diciembre'},
]

export const STATUS_POLICY = {
  PENDING: 'PENDING',
  CANCELED: 'CANCELED',
  COMPLETED: 'COMPLETED',
}

export const PAYMENT_STATUS_OPTIONS = [
  {value: 'PAID', label: 'Paid'},
  {value: 'FAILED', label: 'Failed'},
  {value: 'PENDING', label: 'Pending'},
  {value: 'AUTO_PAY', label: 'Auto Pay'},
  {value: 'NOT_APPLICABLE', label: 'Not Applicable ($0)'},
  {value: 'PAGA_POR_SU_CUENTA', label: 'Paga por su cuenta'},
]
export const DOCUMENT_STATUS_OPTIONS = [
  {value: 'ACTIVE', label: 'Active'},
  {value: 'PENDING', label: 'Pending'},
  {value: 'PROCESSING', label: 'Processing'},
  {value: 'DECLINED', label: 'Declined'},
  {value: 'COMPLETED', label: 'Completed'},
]

// Bootstrap variant type helper
export type BootstrapVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'

// Map Document status → Bootstrap variant
export const getDocumentVariant = (value?: string): BootstrapVariant => {
  const v = (value || '').toString().toUpperCase()
  switch (v) {
    case 'COMPLETED':
      return 'success' // green
    case 'DECLINED':
      return 'danger' // red
    case 'PROCESSING':
    case 'PENDING':
      return 'warning' // amber
    case 'ACTIVE':
      return 'info' // blue/cyan
    default:
      return 'secondary' // neutral gray
  }
}

// Map Payment status → Bootstrap variant
export const getPaymentVariant = (value?: string): BootstrapVariant => {
  const v = (value || '').toString().toUpperCase()
  switch (v) {
    case 'PAID':
      return 'success' // green
    case 'FAILED':
      return 'danger' // red
    case 'PENDING':
      return 'warning' // amber
    case 'AUTO_PAY':
      return 'success'
    case 'NOT_APPLICABLE':
      return 'secondary' // neutral gray
    case 'PAGA_POR_SU_CUENTA':
      return 'warning' // amber
    default:
      return 'secondary'
  }
}

export const INIT_CONTACT_INFO = {
  fullName: '',
  firstName: '',
  middleName: '',
  lastName: '',
  secondLastName: '',
  preferredLenguage: '',
  dateBirth: null, // si usas Date, podría ser new Date() o null
  gender: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  stateEUId: '', // ID puede ser string si viene de un select
  zipCode: '',
  countyFips: '',
  immigrationStatus: '',
  annualIncome1: 0,
  annualIncome2: 0,
  annualIncome3: 0,
  isImportant: false,
}

export const INITIAL_CONTACT_FILTERS: {
  name: string
  createdAfter: string
  createdBefore: string
  effectiveDateAfter: string
  effectiveDateBefore: string
  isImportant: undefined
  isArchived: undefined
  isPolicyHolder: undefined
  withoutPolicy: undefined
  withoutLead: undefined
  medicareUnifiedFilter: string
  carrier: string
  policyType: string
  user: string
  assignedTo: string
  zipCode: string
  state: string
  status: string
  policyStatus: string
  myContacts: undefined
  signatureForm: string
  signatureStatus: string
  preferredLanguage: string
  birthMonth: string
  ageFrom: string
  ageTo: string
  gender: string
  immigrationStatus: string
  policyCreatedFrom: string
  policyCreatedTo: string
  submissionDateFrom: string
  submissionDateTo: string
  completedDateFrom: string
  completedDateTo: string
  organizationId?: string
} = {
  name: '',
  createdAfter: '',
  createdBefore: '',
  effectiveDateAfter: '',
  effectiveDateBefore: '',
  isImportant: undefined,
  isArchived: undefined,
  isPolicyHolder: undefined,
  withoutPolicy: undefined,
  withoutLead: undefined,
  medicareUnifiedFilter: 'ALL_ELIGIBLE',
  carrier: '',
  policyType: '',
  user: '',
  assignedTo: '',
  zipCode: '',
  state: '',
  status: '',
  policyStatus: '',
  myContacts: undefined,
  signatureForm: '',
  signatureStatus: '',
  preferredLanguage: '',
  birthMonth: '',
  ageFrom: '',
  ageTo: '',
  gender: '',
  immigrationStatus: '',
  policyCreatedFrom: '',
  policyCreatedTo: '',
  submissionDateFrom: '',
  submissionDateTo: '',
  completedDateFrom: '',
  completedDateTo: '',
  organizationId: '',
}

export const INITIAL_POLICY_FILTERS: {
  policyName: string
  isImportant: undefined
  isArchived: undefined
  isPolicyHolder: undefined
  hasReminders: undefined
  hasMemberId: undefined
  commissionPayable: undefined
  advisorPaid: undefined
  unassigned: undefined
  ownsCar: undefined
  ownsHome: undefined
  hasAutoPolicy: undefined
  hasHomePolicy: undefined
  carrier: string
  insuranceCategory: string
  policyType: string
  user: string
  assignedTo: string
  zipCode: string
  state: string
  stateEU: string
  stateJurisdictionType: string
  status: string
  documentStatus: string
  paymentStatus: string
  leadSource: string
  signatureForm: string | string[]
  signatureStatus: string
  agentOfRecord: string[]
  immigrationStatus: string
  holderGender: string
  metalLevel: string
  createdAfter: string
  createdBefore: string
  effectiveDateAfter: string
  effectiveDateBefore: string
  paymentStatusDateAfter: string
  paymentStatusDateBefore: string
  advisorPaidDateAfter: string
  advisorPaidDateBefore: string
  advisorChargeback: undefined
  advisorChargebackDateAfter: string
  advisorChargebackDateBefore: string
  canceledAfter: string
  canceledBefore: string
  completedAfter: string
  completedBefore: string
  effectiveYear: string
  carrierStatusGeneric: string
  carrierStatusRaw: string
  carrierLastAsOfDateAfter: string
  carrierLastAsOfDateBefore: string
  carrierIsAgeIn: undefined
  carrierIsDelinquent: undefined
  carrierIsUnpaidPending: undefined
  carrierIsUnpaidPastDue: undefined
} = {
  policyName: '',
  isImportant: undefined,
  isArchived: undefined,
  isPolicyHolder: undefined,
  hasReminders: undefined,
  hasMemberId: undefined,
  commissionPayable: undefined,
  advisorPaid: undefined,
  unassigned: undefined,
  ownsCar: undefined,
  ownsHome: undefined,
  hasAutoPolicy: undefined,
  hasHomePolicy: undefined,
  carrier: '',
  insuranceCategory: '',
  policyType: '',
  user: '',
  assignedTo: '',
  zipCode: '',
  state: '',
  stateEU: '',
  stateJurisdictionType: '',
  status: '',
  documentStatus: '',
  paymentStatus: '',
  leadSource: '',
  signatureForm: [],
  signatureStatus: '',
  agentOfRecord: [],
  immigrationStatus: '',
  holderGender: '',
  metalLevel: '',
  createdAfter: '',
  createdBefore: '',
  effectiveDateAfter: '',
  effectiveDateBefore: '',
  paymentStatusDateAfter: '',
  paymentStatusDateBefore: '',
  advisorPaidDateAfter: '',
  advisorPaidDateBefore: '',
  advisorChargeback: undefined,
  advisorChargebackDateAfter: '',
  advisorChargebackDateBefore: '',
  canceledAfter: '',
  canceledBefore: '',
  completedAfter: '',
  completedBefore: '',
  effectiveYear: '',
  carrierStatusGeneric: '',
  carrierStatusRaw: '',
  carrierLastAsOfDateAfter: '',
  carrierLastAsOfDateBefore: '',
  carrierIsAgeIn: undefined,
  carrierIsDelinquent: undefined,
  carrierIsUnpaidPending: undefined,
  carrierIsUnpaidPastDue: undefined,
}

/*
export const INITIAL_POLICY_FILTERS_2: {
  policyName: '',
  carrier: '',
  createdAt: '',
  effectiveDate: '',
  completed: '',
  canceled: '',
  archived: '',
  isImportant: false,
  isArchived: false,
  isReminders: false,
  policyType: '',
  user: '',
  agent: '',
  status: '',
  stateEU: '',
  assignedTo: '',
  zipCode: '',
  effectiveYear: '',
  insuranceCategory: '',
}*/

const DEFAULT_EFFECTIVE_DATE = new Date().toISOString().slice(0, 10)

export const INIT_IFORM: IForm = {
  queryType: 'individual',
  effectiveDate: DEFAULT_EFFECTIVE_DATE,
  year: getYearFromEffectiveDate(DEFAULT_EFFECTIVE_DATE),
  householdIncome: '0',
  market: 'FFM',
  countyfips: '',
  countyfipsName: '',
  state: 'FL',
  zipCode: '',
  limit: 10,
  offset: 0,
  order: 'asc',
  utilizationLevel: '',
  numberOfMembers: '1',
  personOne: {
    age: '',
    dob: '',
    gender: '',
    isPregnant: false,
    pregnantWith: '',
    isParent: false,
    usesTobacco: false,
    lastTobaccoUseDate: '',
    hasMec: true,
    aptcEligible: true,
    utilizationLevel: '',
    relationship: 'self',
    doesNotCohabitate: '',
    currentEnrollment: null,
  },
  people: [],
}

export const TABS_ONE_USER = ['Overview', 'Edit user', 'Channels', 'Deductions'] as const
// export const TABS_ONE_USER = ['Overview', 'Edit user', 'Deductions', 'Transactions'] as const

export const DEDUCTION_TYPE_OPTIONS = [
  {value: '', label: 'Select type'},
  {value: 'FIXED_AMOUNT', label: 'Fixed Amount'},
  {value: 'PERCENTAGE', label: 'Percentage'},
]

export const RELATIONSHIP_OPTIONS = [
   {value: 'Self', label: 'Self'},
   {value: 'Spouse', label: 'Spouse'},
   {value: 'Child', label: 'Child'},
   {value: 'Mother or Father', label: 'Mother or Father'},
   {value: 'Life Partner', label: 'Life Partner'},
   {value: 'Brother or Sister', label: 'Brother or Sister'},
   {value: 'Nephew or Niece', label: 'Nephew or Niece'},
   {value: 'Stepson or Stepdaughter', label: 'Stepson or Stepdaughter'},
   {value: 'Ward', label: 'Ward'},
   {value: 'Grandson or Granddaughter', label: 'Grandson or Granddaughter'},
   {value: 'Sponsored Dependent', label: 'Sponsored Dependent'},
   {value: 'Collateral Dependent', label: 'Collateral Dependent'},
   {value: 'Foster Child', label: 'Foster Child'},
   {value: 'Ex-Spouse', label: 'Ex-Spouse'},
   {value: 'Adopted Child', label: 'Adopted Child'},
   {value: 'Court Appointed Guardian', label: 'Court Appointed Guardian'},
   {value: 'Dependent of a Minor Dependent', label: 'Dependent of a Minor Dependent'},
   {value: 'Other Relative', label: 'Other Relative'},
   {value: 'Other Relationship', label: 'Other Relationship'},
   
 ]
